import React, { useState, useEffect, useRef } from "react";
import Moment from "moment";
import usePageTitle from 'hooks/usePageTitle';
import { Joyride } from 'react-joyride';
import { getJoyrideScrollOffset } from 'utils/joyrideConfig';
import { useRecordTour } from 'hooks/useRecordTour';
import PageMenu from "../../../components/blocks/PageMenu/PageMenu";
import Pagination from "../../../components/blocks/Pagination/Pagination";
import CardLinkColor from "../../blocks/CardsLink/CardLinkColor";
import RecordsGrid from "../../blocks/Grids/RecordsGrid";
import useCheckTypes from "../../../hooks/useCheckRecordStatusAndTypes";
import { IRecordResponse } from "@solimat/solimat-web-endpoint/lib/record/dto";
import useGetAllAvailableRecordByUser from "../../../services/recordSheets/useGetAllAvailableRecordByUser";
import CardNewManagement from "components/CardNewManagement/CardNewManagement";
import useSortDataRecords from "hooks/useSortDataRecords";
import Loader from "components/blocks/Loader/Loader";
import useGetRole from "hooks/useGetRole";
import NoData from "../noData/NoData";
import Icon from "../../blocks/Icon/Icon";
import "./Record.scss";

const Record: React.FC = () => {
  usePageTitle('Mi Historial');
  const [records, setRecords] = useState<IRecordResponse[]>([]);
  const [recordsCards, setRecordsCards] = useState<IRecordResponse[]>([]);
  const [ascendingCards, setAscendingCards] = useState(true);
  const [viewCard, setViewCard] = useState(true);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const {
    getAllAvailableRecordByUserS,
    loadingGetAllAvailableRecordByUser,
    userId,
  } = useGetAllAvailableRecordByUser();
  const { orderDescendingData, orderAscendingData } = useSortDataRecords();
  const { checkCoverageType, checkContingencyType } = useCheckTypes();

  const [errorRequest, setErrorRequest] = useState(null);
  // State pagination
  const [currentPageItems, setCurrentPageItems] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [paginationPageSizes] = useState<string[]>([
    "2",
    "4",
    "8",
    "12",
    "16",
    "20",
    "Todos",
  ]);
  // Filtrado por expediente
  const q = search.trim().toLowerCase();
  const matchesSearch = (r: any) =>
    String(r.ExpedienteId ?? "").toLowerCase().includes(q) ||
    String(r.descripcionContingencia ?? "").toLowerCase().includes(q);

  const filteredCards = q ? recordsCards.filter(matchesSearch) : recordsCards;
  const filteredGrid  = q ? records.filter(matchesSearch) : records;
  const countTodas    = filteredCards.length;

  // Get current recordCard
  const indexOfLastItem = currentPageItems * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCards.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginateCards = (pageNumber: number) => setCurrentPageItems(pageNumber);
  const handleOnChangeItemsPerPage = (dropdownValue: string) => {
    let numItems = itemsPerPage;
    if (dropdownValue === "Todos") {
      numItems = recordsCards.length;
    } else {
      numItems = parseInt(dropdownValue);
    }
    setItemsPerPage(numItems);
    paginateCards(1);
  };

  const { isImpersonation, isSIP } = useGetRole;

  const hasRecords = records.length > 0;
  const { run, tourKey, steps, handleCallback, startTour } = useRecordTour(hasRecords);

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('solimat:startRecordTour', handler);
    return () => window.removeEventListener('solimat:startRecordTour', handler);
  }, [startTour]);

  const JOYRIDE_PROPS = {
    continuous: true, scrollToFirstStep: true,
    locale: { back: 'Anterior', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', nextWithProgress: 'Siguiente ({current} de {total})', open: 'Iniciar tour de ayuda', skip: 'Omitir tour' },
    options: { primaryColor: '#0F1F3D', zIndex: 10000, overlayColor: 'rgba(15,47,73,0.45)', showProgress: true, buttons: ['back','skip','close','primary'] } as any,
    styles: { beaconInner: { backgroundColor: '#F97316' }, beaconOuter: { borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.2)' }, tooltip: { borderRadius: 8, fontSize: 14 }, tooltipTitle: { fontSize: 15, fontWeight: 700, textAlign: 'left' as const }, tooltipContent: { textAlign: 'left' as const } },
  };

  useEffect(() => {
    getAllAvailableRecordByUserS(userId, handleSetAllRecords);
    return () => {
      getAllAvailableRecordByUserS(userId, handleSetAllRecords);
    }
  }, []);

  const handleSetAllRecords = (formatRes: any) => {
    orderDescendingData(formatRes, setRecords);
    orderDescendingData(formatRes, setRecordsCards);
  };

  const handleUpdateRecords = (recordsOrdered: any) => {
    setRecordsCards(recordsOrdered);
  };

  const orderByAscending = () => {
    // by old to new
    orderAscendingData(recordsCards, handleUpdateRecords);
    setAscendingCards(false);
  };

  const orderByDescending = () => {
    // by new to old
    orderDescendingData(recordsCards, handleUpdateRecords);
    setAscendingCards(true);
  };

  const handleToggleViewCard = () => {
    setViewCard(!viewCard);
  };

  const breadcrumbs = ["Historial"];
  if (records === null ) {
    return (
      <>
        <PageMenu title="Historial" subtitle="Tus expedientes y procesos activos" />
        <div className="container">
          <NoData message={'No hay datos de historial para mostrar.'}/>
        </div>
      </>
    );
  } else {
    return (
      <>
        <Joyride
          key={tourKey}
          steps={steps}
          run={run}
          onEvent={handleCallback}
          {...JOYRIDE_PROPS}
          options={{
            ...JOYRIDE_PROPS.options,
            scrollOffset: getJoyrideScrollOffset(),
          }}
        />
        <PageMenu title="Historial" subtitle="Tus expedientes y procesos activos" breadcrumbs={breadcrumbs}>
          {records.length ? (
            <div className="btns-header-container">
              <button
                className={`btn-toggle-view ${viewCard ? "selected" : ""}`}
                onClick={handleToggleViewCard}
              >
                <span className="e-icons e-Level4"></span>
              </button>
              <button
                className={`btn-toggle-view ${viewCard ? "" : "selected"}`}
                onClick={handleToggleViewCard}
              >
                <span className="e-icons e-M_Bullets"></span>
              </button>
            </div>
          ) : null}
        </PageMenu>
        <div className="container">
          <div className="row">
            <div className="col">
              {errorRequest ? (
                <p className="loader-text">
                  Lo sentimos. Se ha producido un error.
                </p>
              ) : null}
              {loadingGetAllAvailableRecordByUser && records.length < 1 ? (
                <Loader activeLoader label="Descargando prestaciones" />
              ) : null}
              {!loadingGetAllAvailableRecordByUser &&
                records.length < 1 &&
                !errorRequest && (
                  <NoData message={'No hay datos de historial para mostrar.'}/>
                )}
              {records.length > 0 && (
                <>
                  <div className="tabs" style={{ marginBottom: 0 }}>
                    <button className="tabs__tab is-active">
                      Prestaciones
                      <span className="tabs__count">{countTodas}</span>
                    </button>
                  </div>
                  <div className="record-search">
                    <Icon name="search" size={16} />
                    <input
                      ref={searchRef}
                      className="record-search__input"
                      type="text"
                      placeholder="Buscar por contingencia…"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); paginateCards(1); }}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {search && (
                      <button
                        className="record-search__clear"
                        onClick={() => { setSearch(""); paginateCards(1); }}
                        aria-label="Limpiar búsqueda"
                      >
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>
                  {filteredCards.length === 0 && q && (
                    <p className="record-search__empty">Sin resultados para «{search}».</p>
                  )}
                  {viewCard ? (
                    <>
                      <div className="row">
                        <div className="col-12 btns-upon-box-container">
                          {ascendingCards ? (
                            <button onClick={() => orderByAscending()}>
                              Fecha reciente{" "}
                              <span className="e-icons e-Filter descending"></span>
                            </button>
                          ) : (
                            <button onClick={() => orderByDescending()}>
                              Fecha antigua{" "}
                              <span className="e-icons e-Filter ascending"></span>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="row">
                        {currentItems.map((item: any) => {
                          return (
                            <div
                              className="col-sm-6 col-md-4 col-lg-4 col-xl-4 container-card-link-color"
                              key={item.id}
                            >
                              <CardLinkColor
                                
                                tipoPrestacion={item.TipoPrestacion}
                                contingencia={item.contingencia}
                                title={checkContingencyType(
                                  parseInt(item.contingencia)
                                )}
                                expedienteId={item.ExpedienteId}
                                tieneExtincion={item.tieneExtincion}
                                descripcionExtincion={item.descripcionExtincion}
                                oficioId={item.oficioId}
                                procesoId={item.procesoId}
                                linkTo={`/historial/${item.TipoPrestacion}_${item.id}`}
                                descripcion={checkCoverageType(
                                  parseInt(item.cobertura)
                                )}
                                info={
                                  item.status === "0" ||
                                    item.fechaInicio !== null
                                    ? ` ${Moment(item.fechaInicio).format("DD/MM/YYYY")}`
                                    : ` `
                                }
                                info2={
                                  item.status === "0" || item.fechaFin !== null
                                    ? ` ${Moment(item.fechaFin).format("DD/MM/YYYY")}`
                                    : ` `
                                }
                                statusClass={
                                  item.TipoPrestacion === 1
                                    ? "bright"
                                    : item.TipoPrestacion === 2
                                      ? "dark"
                                      : item.TipoPrestacion === 3
                                        ? "clear"
                                        : item.cobertura === 0 ||
                                          item.contingencia === 0
                                          ? "undetermined"
                                          : ""
                                }
                              />
                            </div>
                          );
                        })}
                       {/*  {(!isImpersonation() || isSIP())&&
                          <CardNewManagement />
                        } */}
                        {filteredCards.length >= 5 ? (
                          <div className="col-12">
                            <Pagination
                              itemsPerPage={itemsPerPage}
                              totalItems={filteredCards.length}
                              paginate={paginateCards}
                              currentPage={currentPageItems}
                              handleOnChangeItemsPerPage={
                                handleOnChangeItemsPerPage
                              }
                              paginationPageSizes={paginationPageSizes}
                            />
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="records__grid">
                      <RecordsGrid arrData={filteredGrid} props={{}} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
};

export default Record;